import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type DiscountType = 'percentage' | 'fixed';

/** 
 * Campaign scope:
 * - 'all'      → tüm ürünler
 * - 'lithium' | 'agm' | 'gel' → kategori bazlı
 * - 'specific' → belirli ürünler (productIds)
 * - 'category_and_specific' → hem kategori hem belirli ürünler (union)
 */
export type CampaignTarget = 'all' | 'lithium' | 'agm' | 'gel' | 'specific' | 'category_and_specific';

export interface Campaign {
  id: string;
  name: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  target: CampaignTarget;
  targetCategories: string[];  // for category_and_specific
  productIds: string[];
  active: boolean;
  startDate: string;
  endDate: string;
  badgeLabel: string;
  /** If linked to a special day id */
  specialDayId?: string;
}

interface CampaignContextValue {
  campaigns: Campaign[];
  addCampaign: (c: Omit<Campaign, 'id'>) => void;
  updateCampaign: (c: Campaign) => void;
  deleteCampaign: (id: string) => void;
  toggleActive: (id: string) => void;
  getDiscountedPrice: (productId: string, category: string, originalPrice: number) => { price: number; campaign: Campaign | null };
}

const STORAGE_KEY = 'stingray_campaigns';
const CampaignContext = createContext<CampaignContextValue | null>(null);

function loadCampaigns(): Campaign[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Campaign[];
  } catch { /* ignore */ }
  return [];
}

export function CampaignProvider({ children }: { children: ReactNode }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>(loadCampaigns);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns)); } catch { /* ignore */ }
  }, [campaigns]);

  const addCampaign = useCallback((c: Omit<Campaign, 'id'>) => {
    setCampaigns((prev) => [...prev, { ...c, id: 'camp-' + Date.now().toString(36) }]);
  }, []);

  const updateCampaign = useCallback((c: Campaign) => {
    setCampaigns((prev) => prev.map((x) => (x.id === c.id ? c : x)));
  }, []);

  const deleteCampaign = useCallback((id: string) => {
    setCampaigns((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toggleActive = useCallback((id: string) => {
    setCampaigns((prev) => prev.map((x) => (x.id === id ? { ...x, active: !x.active } : x)));
  }, []);

  const getDiscountedPrice = useCallback(
    (productId: string, category: string, originalPrice: number): { price: number; campaign: Campaign | null } => {
      const now = new Date();
      const applicable = campaigns.find((c) => {
        if (!c.active) return false;
        const start = new Date(c.startDate);
        const end = new Date(c.endDate);
        if (now < start || now > end) return false;

        switch (c.target) {
          case 'all': return true;
          case 'lithium':
          case 'agm':
          case 'gel':
            return c.target === category;
          case 'specific':
            return c.productIds.includes(productId);
          case 'category_and_specific':
            return (
              (c.targetCategories || []).includes(category) ||
              c.productIds.includes(productId)
            );
          default: return false;
        }
      });

      if (!applicable) return { price: originalPrice, campaign: null };

      let discounted = originalPrice;
      if (applicable.discountType === 'percentage') {
        discounted = originalPrice * (1 - applicable.discountValue / 100);
      } else {
        discounted = originalPrice - applicable.discountValue;
      }
      return { price: Math.max(0, Math.round(discounted)), campaign: applicable };
    },
    [campaigns]
  );

  return (
    <CampaignContext.Provider value={{ campaigns, addCampaign, updateCampaign, deleteCampaign, toggleActive, getDiscountedPrice }}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const ctx = useContext(CampaignContext);
  if (!ctx) throw new Error('useCampaign must be used within CampaignProvider');
  return ctx;
}
